import {
  QuestionAnsweredStatus,
  QuestionSearchSort,
  SearchQuestionsItemsQuery,
  SearchQuestionsTotalCountQuery,
} from '@/types/bff/gql-schema';
import { RealtimeQnaCardVo } from '@/types/qna';
import { absoluteDate } from '@/utils/format';
import { QueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';
import { useSearchQuestionsItemsQuery, useSearchQuestionsTotalCountQuery } from './qna.gql.impl';

interface Props {
  categoryIds: string[];
  page?: number;
  size?: number;
}

const DEFAULT_SIZE = 20;

const parseRealtimeQnaCardVo = (data: SearchQuestionsItemsQuery): RealtimeQnaCardVo[] => {
  const items = data.searchQuestions.items.map((item) => {
    return {
      answeredStatus: QuestionAnsweredStatus.Answered,
      answer: null,
      answererCount: 0,
      answererProfileImageUrls: [],
      question: {
        accessibleType: item.accessibleType,
        content: item.content,
        createdAt: item.createdAt,
        isBlockedByMe: item.isBlockedByMe,
        isLikedByMe: false,
        isMine: false,
        likeCount: 0,
        questionPid: item.publicId,
        title: item.title ?? '',
        attachments: item.attachments.map((attachment) => ({
          blindType: attachment.blindType,
          questionAttachmentId: attachment.id,
        })),
        category: {
          name: item.category.name,
          questionCategoryId: item.category.id,
          iconImageUrl: item.category.iconImagePath,
        },
        tags: item.tags,
        user: {
          name: item.userMaskedName,
          userId: item.userId,
        },
      },
      questionCreatedAt: absoluteDate(item.createdAt),
      answerCreatedAt: null,
      answeredStatusText: '',
    };
  });
  return items;
};

const parseRealtimeQnaCardTotalCount = (data: SearchQuestionsTotalCountQuery): number => {
  return data.searchQuestions.totalCount;
};

const useSearchQuestionsInfinite = ({ categoryIds, page = 1, size = DEFAULT_SIZE }: Props) => {
  const { data: totalCount = 0, isLoading: isLoadingTotalCount } = useSearchQuestionsTotalCountQuery(
    {
      request: {
        categoryIds,
        page: 1,
        size,
        sort: QuestionSearchSort.Recent,
      },
    },
    {
      staleTime: 5000,
      cacheTime: 5000,
      select: parseRealtimeQnaCardTotalCount,
    }
  );

  const totalPage = Math.ceil(totalCount / size);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQuery(
    useSearchQuestionsItemsQuery.getKey({
      request: {
        categoryIds,
        page: 1,
        size,
        sort: QuestionSearchSort.Recent,
      },
    }),
    ({ pageParam }) => {
      const requestPage = pageParam ?? page;

      return useSearchQuestionsItemsQuery.fetcher({
        request: {
          categoryIds,
          page: requestPage,
          size,
          sort: QuestionSearchSort.Recent,
        },
      })();
    },
    {
      enabled: !!totalPage,
      getNextPageParam: (_, pages) => {
        if (pages.length < totalPage) {
          return pages.length + 1;
        }
        return undefined;
      },
    }
  );

  const qnaList = useMemo(() => {
    const qnas = data?.pages?.flatMap((page) => parseRealtimeQnaCardVo(page)) ?? [];
    return uniqBy(qnas, 'question.questionPid');
  }, [data]);

  return {
    totalPage,
    questions: qnaList,
    isLoading: isLoadingTotalCount || isLoading,
    fetchNextPage,
    hasNextPage,
  };
};

export default useSearchQuestionsInfinite;

useSearchQuestionsInfinite.prefetchInfiniteQuery = async (
  queryClient: QueryClient,
  variables: Omit<Props, 'totalPage'>
) => {
  const request = {
    categoryIds: variables.categoryIds,
    page: 1,
    size: variables.size ?? DEFAULT_SIZE,
    sort: QuestionSearchSort.Recent,
  };

  await queryClient.prefetchInfiniteQuery(useSearchQuestionsItemsQuery.getKey({ request }), () =>
    useSearchQuestionsItemsQuery.fetcher({
      request,
    })()
  );
};
